import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

export const serif = { fontFamily: 'var(--v3-font-serif)' } as const
export const mono = { fontFamily: 'var(--v3-font-mono)' } as const

/** String-path link: the typed route tree does not know `$idOrCode` routes. */
export function LedgerLink({ to, className, children }: { to: string; className?: string; children: ReactNode }) {
  return (
    <Link
      to={to as '/locations'}
      className={`text-[var(--color-accent)] underline decoration-[var(--color-border)] underline-offset-4 transition-colors hover:decoration-[var(--color-accent)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] ${className ?? ''}`}
    >
      {children}
    </Link>
  )
}

export const locPath = (idOrCode: string) => `/locations/${encodeURIComponent(idOrCode)}`

export function Header({
  eyebrow,
  title,
  standfirst,
  aside,
}: {
  eyebrow: ReactNode
  title: ReactNode
  standfirst?: ReactNode
  aside?: ReactNode
}) {
  return (
    <header className="border-b-2 border-[var(--v3-rule-strong)] pb-6">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted)]">{eyebrow}</p>
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
        <h1 className="text-[36px] font-semibold leading-[1.1] tracking-[-0.015em]" style={serif}>
          {title}
        </h1>
        {aside}
      </div>
      {standfirst ? (
        <p className="mt-3 max-w-[60ch] text-[16px] leading-[1.55] text-[var(--color-muted)]" style={serif}>
          {standfirst}
        </p>
      ) : null}
    </header>
  )
}

export function Section({
  title,
  note,
  aside,
  children,
}: {
  title: string
  note?: ReactNode
  aside?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between gap-4 border-b border-[var(--v3-rule-strong)] pb-2">
        <h2 className="text-[20px] font-semibold tracking-[-0.01em]" style={serif}>
          {title}
        </h2>
        <span className="text-[13px]">{aside}</span>
      </div>
      {note ? (
        <p className="mt-2 max-w-[62ch] text-[14px] italic leading-[1.5] text-[var(--color-muted)]" style={serif}>
          {note}
        </p>
      ) : null}
      {children}
    </section>
  )
}

export function Note({ children, tone }: { children: ReactNode; tone?: 'error' }) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      className={`border-b border-[var(--color-border)] py-8 text-[16px] italic ${
        tone === 'error' ? 'text-[var(--color-accent)]' : 'text-[var(--color-muted)]'
      }`}
      style={serif}
    >
      {children}
    </p>
  )
}

export function Facts({ items }: { items: [string, ReactNode][] }) {
  const shown = items.filter(([, v]) => v !== null && v !== undefined && v !== '')
  if (shown.length === 0) return <Note>Nothing on record.</Note>
  return (
    <dl className="m-0">
      {shown.map(([k, v]) => (
        <div key={k} className="flex items-baseline gap-6 border-b border-[var(--color-border)] py-2.5">
          <dt className="w-[150px] shrink-0 text-[12.5px] text-[var(--color-muted)]">{k}</dt>
          <dd className="m-0 min-w-0 text-[15px]" style={serif}>
            {v}
          </dd>
        </div>
      ))}
    </dl>
  )
}

const TONE: Record<string, { color: string; on: boolean }> = {
  up: { color: 'var(--v3-ok)', on: true },
  online: { color: 'var(--v3-ok)', on: true },
  active: { color: 'var(--v3-ok)', on: true },
  down: { color: 'var(--color-accent)', on: true },
  offline: { color: 'var(--color-accent)', on: true },
  degraded: { color: 'var(--color-accent)', on: false },
}

export function Status({ label }: { label: string }) {
  const t = TONE[label.toLowerCase()] ?? { color: 'var(--v3-off)', on: false }
  return (
    <span className="inline-flex items-center gap-2 text-[13px]" style={{ color: t.color, ...serif }}>
      <span
        aria-hidden="true"
        className="size-[7px] rounded-full"
        style={t.on ? { background: t.color } : { boxShadow: `inset 0 0 0 1.5px ${t.color}` }}
      />
      {label}
    </span>
  )
}

/** Big serif figure with a small caption, for ruled tallies. */
export function Figure({ label, value, tone }: { label: string; value: number | string; tone?: 'bad' | 'ok' }) {
  const color = tone === 'bad' ? 'var(--color-accent)' : tone === 'ok' ? 'var(--v3-ok)' : 'var(--color-fg)'
  return (
    <div className="border-l border-[var(--color-border)] pl-4 first:border-l-0 first:pl-0">
      <p className="text-[34px] font-semibold leading-none tabular-nums" style={{ ...serif, color }}>
        {value}
      </p>
      <p className="mt-2 text-[12px] uppercase tracking-[0.12em] text-[var(--color-muted)]">{label}</p>
    </div>
  )
}

export function SkeletonRows({ n = 6 }: { n?: number }) {
  return (
    <output aria-busy="true" aria-label="Loading" className="mt-6 block divide-y divide-[var(--color-border)]">
      {Array.from({ length: n }, (_, i) => `s${i}`).map((k) => (
        <div key={k} className="flex h-10 items-center gap-8">
          <span className="v3-skeleton w-24" />
          <span className="v3-skeleton w-56" />
        </div>
      ))}
    </output>
  )
}
